<template>
  <div 
    :class="[
      'mv-player bg-black rounded-lg overflow-hidden relative group h-full transition-all duration-200',
      selectedSource && !isMuted && volume > 0 && (!leftChannelMuted || !rightChannelMuted) 
        ? 'ring-2 ring-green-500/80 shadow-lg shadow-green-500/20' 
        : ''
    ]"
  >
    <!-- Video Element -->
    <video
      ref="videoElement"
      class="w-full h-full object-contain"
      :muted="isMuted"
      autoplay
      playsinline
    ></video>

    <!-- Controls Overlay -->
    <div
      class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col"
    >
      <!-- Top Controls (now empty, just for spacing) -->
      <div class="p-2">
        <!-- Empty space for top area -->
      </div>

      <!-- Center Controls -->
      <div class="flex-1 flex items-center justify-center">
        <!-- Loading Spinner -->
        <div v-if="connectionStatus === 'connecting'" class="flex flex-col items-center gap-2">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          <span class="text-white text-sm">Connecting...</span>
        </div>
        
        <!-- Play/Pause Button -->
        <!-- <button
          v-else
          @click="togglePlayPause"
          class="text-white hover:text-blue-400 transition-colors"
          :title="isPlaying ? 'Pause' : 'Play'"
        >
          <svg v-if="!isPlaying" class="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
              clip-rule="evenodd"
            ></path>
          </svg>
          <svg v-else class="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button> -->
      </div>

      <!-- Bottom Controls -->
      <div class="p-2 lg:flex justify-between items-end">
        <!-- Bottom Left - Source Select -->
        <div v-if="showSourceControl" class="flex items-center gap-2">
          <select
            v-model="selectedSource"
            @change="changeSource"
            class="bg-gray-800 text-white text-xs px-2 py-1 rounded-sm border border-gray-600 focus:border-blue-500 focus:outline-hidden"
            title="Select video source"
          >
            <option value="">Select Source</option>
            <option v-for="source in availableSources" :key="source.id" :value="source.url">
              {{ source.name }}
            </option>
          </select>
        </div>

        <!-- Bottom Right - Audio Controls -->
        <div class="flex items-center gap-2 text-xs mt-4 lg:mt-0">
          <!-- Audio Pair Select -->
          <label class="text-white hidden lg:inline-block">Audio:</label>
          <select
            v-model="selectedAudioPair"
            @change="changeAudioPair"
            :disabled="connectionStatus !== 'connected'"
            :class="[
              'px-2 py-1 rounded-sm border text-xs',
              connectionStatus !== 'connected'
                ? 'bg-gray-700 text-gray-400 border-gray-600 cursor-not-allowed'
                : 'bg-gray-800 text-white border-gray-600 focus:border-blue-500 focus:outline-hidden'
            ]"
            title="Select audio channel pair"
          >
            <option v-for="pair in audioPairs" :key="pair.id" :value="pair.id">
              {{ pair.name }}
            </option>
          </select>

          <!-- Audio Mute Controls -->
          <div class="flex items-center gap-1">
            <button
              @click="toggleLeftChannel"
              :disabled="connectionStatus !== 'connected'"
              :class="[
                'px-2 py-1 rounded-sm text-xs font-medium transition-colors',
                connectionStatus !== 'connected'
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : leftChannelMuted
                  ? 'bg-red-600 text-white hover:bg-red-500'
                  : 'bg-green-600 text-white hover:bg-green-500',
              ]"
              title="Toggle Left Channel"
            >
              L
            </button>

            <button
              @click="toggleRightChannel"
              :disabled="connectionStatus !== 'connected'"
              :class="[
                'px-2 py-1 rounded-sm text-xs font-medium transition-colors',
                connectionStatus !== 'connected'
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : rightChannelMuted
                  ? 'bg-red-600 text-white hover:bg-red-500'
                  : 'bg-green-600 text-white hover:bg-green-500',
              ]"
              title="Toggle Right Channel"
            >
              R
            </button>

          </div>

          <!-- Unified Volume/Mute Control -->
          <div class="flex items-center gap-2">
            <!-- Volume/Mute Button -->
            <button
              @click="toggleMasterAudio"
              :disabled="connectionStatus !== 'connected'"
              :class="[
                'px-2 py-1 rounded-sm text-xs transition-colors h-6 flex items-center justify-center',
                connectionStatus !== 'connected'
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : (leftChannelMuted && rightChannelMuted)
                  ? 'bg-red-600 text-white hover:bg-red-500'
                  : volume === 0
                  ? 'bg-orange-600 text-white hover:bg-orange-500'
                  : 'bg-gray-600 text-white hover:bg-gray-500',
              ]"
              :title="connectionStatus !== 'connected' ? 'Connect to stream first' : getVolumeButtonTitle()"
            >
              <!-- Volume Icon - changes based on volume level and channel mute state -->
              <svg 
                v-if="(leftChannelMuted && rightChannelMuted) || volume === 0"
                class="w-4 h-4" 
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <!-- Muted -->
                <g>
                  <path d="M14.68 6.06a0.29 0.29 0 0 0 0.07 -0.18V3.75a1 1 0 0 0 -1.75 -0.7L7.12 9a0.25 0.25 0 0 1 -0.18 0.08H4.75a2 2 0 0 0 -2 2v3.41a2 2 0 0 0 1.42 1.91 0.23 0.23 0 0 0 0.25 -0.06Z" fill="currentColor" stroke-width="1"></path>
                  <path d="M23.71 1.71a1 1 0 0 0 0 -1.41 1 1 0 0 0 -1.42 0l-22 22a1 1 0 0 0 0 1.41 0.79 0.79 0 0 0 0.15 0.12 1 1 0 0 0 1.27 -0.12l6.11 -6.12a0.27 0.27 0 0 1 0.36 0L13 22.46a1 1 0 0 0 1.71 -0.71v-11a0.29 0.29 0 0 1 0.07 -0.18Z" fill="currentColor" stroke-width="1"></path>
                </g>
              </svg>
              <svg 
                v-else-if="volume < 50 && volume !== 0"
                class="w-4 h-4" 
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <!-- Low Volume -->
              <g>
                <path d="M13.79 2.29 7.87 8.22a0.29 0.29 0 0 1 -0.18 0.07H5.5a2 2 0 0 0 -2 2v3.42a2 2 0 0 0 2 2h2.19a0.24 0.24 0 0 1 0.18 0.07l5.92 5.92a1 1 0 0 0 1.71 -0.7V3a1 1 0 0 0 -1.71 -0.71Z" fill="currentColor" stroke-width="1"></path>
                <path d="M18.64 7.5a1 1 0 1 0 -1.42 1.41 4.38 4.38 0 0 1 0 6.18 1 1 0 0 0 0 1.41 1 1 0 0 0 1.42 0 6.37 6.37 0 0 0 0 -9Z" fill="currentColor" stroke-width="1"></path>
              </g>
              </svg>
              <svg 
                v-else
                class="w-4 h-4" 
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <!-- High Volume -->
                <g>
                  <path d="M12 2.29 6.12 8.22a0.29 0.29 0 0 1 -0.18 0.07H3.75a2 2 0 0 0 -2 2v3.42a2 2 0 0 0 2 2h2.19a0.24 0.24 0 0 1 0.18 0.07L12 21.7a1 1 0 0 0 1.71 -0.7V3A1 1 0 0 0 12 2.29Z" fill="currentColor" stroke-width="1"></path>
                  <path d="M19.34 5a1 1 0 0 0 -1.42 0 1 1 0 0 0 0 1.41 7.94 7.94 0 0 1 0 11.24 1 1 0 0 0 0 1.41 1 1 0 0 0 1.42 0 9.95 9.95 0 0 0 0 -14.06Z" fill="currentColor" stroke-width="1"></path>
                  <path d="M16.89 7.5a1 1 0 1 0 -1.42 1.41 4.38 4.38 0 0 1 0 6.18 1 1 0 0 0 0 1.41 1 1 0 0 0 1.42 0 6.37 6.37 0 0 0 0 -9Z" fill="currentColor" stroke-width="1"></path>
                </g>
              </svg>
            </button>

            <!-- Horizontal Volume Slider -->
            <input
              type="range"
              v-model="volume"
              @input="changeVolume"
              :disabled="connectionStatus !== 'connected'"
              min="0"
              max="100"
              :class="[
                'w-20 h-2 rounded-lg appearance-none slider',
                connectionStatus !== 'connected' 
                  ? 'bg-gray-700 cursor-not-allowed' 
                  : 'bg-gray-600 cursor-pointer'
              ]"
              :title="connectionStatus !== 'connected' ? 'Connect to stream first' : `Volume: ${volume}%`"
            />
            
            <!-- Volume Percentage -->
            <span 
              :class="[
                'text-xs w-8',
                connectionStatus !== 'connected' ? 'text-gray-400' : 'text-white'
              ]"
            >
              {{ volume }}%
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Top Left Status Area -->
    <div class="absolute top-2 left-2 flex items-center gap-2 max-w-[calc(100%-16rem)]">
      <!-- Source Name -->
      <div v-if="currentSourceName" class="bg-black/70 text-white text-xs px-2 py-1 rounded-sm">
        {{ currentSourceName }}
      </div>

    

      <!-- Connection Status Indicator -->
      <div
        :class="[
          'w-3 h-3 rounded-full border border-white/30 shrink-0',
          connectionStatus === 'connected'
            ? 'bg-green-500'
            : connectionStatus === 'connecting'
            ? 'bg-yellow-500'
            : 'bg-red-500',
        ]"
        :title="
          connectionStatus === 'connected'
            ? 'Connected'
            : connectionStatus === 'connecting'
            ? 'Connecting...'
            : 'Disconnected'
        "
      ></div>

      <!-- Last Log Entry -->
      <!-- <div 
        v-if="lastLogEntry" 
        :class="[
          'text-xs px-2 py-1 rounded-sm max-w-xs truncate',
          lastLogEntry.toLowerCase().includes('error') 
            ? 'bg-red-600/80 text-white' 
            : 'bg-black/50 text-white'
        ]"
        :title="lastLogEntry"
      >
        {{ lastLogEntry }}
      </div> -->
    </div>

    <!-- Top Right Controls -->
    <div class="absolute top-2 right-2 flex items-center gap-1">
      <!-- Action Buttons -->
      <button
        @click="toggleFullscreen"
        :disabled="connectionStatus !== 'connected'"
        :class="[
          'px-2 py-1 rounded-sm text-xs transition-colors',
          connectionStatus !== 'connected'
            ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
            : 'bg-gray-600 text-white hover:bg-gray-500'
        ]"
        :title="connectionStatus !== 'connected' ? 'Connect to stream first' : 'Fullscreen'"
      >
        <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path
            fill-rule="evenodd"
            d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z"
            clip-rule="evenodd"
          ></path>
        </svg>
      </button>

      <!-- <button
        @click="stopSource"
        :disabled="connectionStatus !== 'connected'"
        :class="[
          'px-2 py-1 rounded-sm text-xs transition-colors',
          connectionStatus !== 'connected'
            ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
            : 'bg-red-600 text-white hover:bg-red-500'
        ]"
        :title="connectionStatus !== 'connected' ? 'Connect to stream first' : 'Stop Stream'"
      >
        <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z"
            clip-rule="evenodd"
          ></path>
        </svg>
      </button> -->

      <button
        @click="ejectSource"
        :disabled="connectionStatus !== 'connected'"
        :class="[
          'px-2 py-1 rounded-sm text-xs transition-colors',
          connectionStatus !== 'connected'
            ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
            : 'bg-gray-600 text-white hover:bg-gray-500'
        ]"
        :title="connectionStatus !== 'connected' ? 'Connect to stream first' : 'Eject Source'"
      >
        <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path
            fill-rule="evenodd"
            d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z"
            clip-rule="evenodd"
          ></path>
        </svg>
      </button>

      <!-- Close Button (if needed) -->
      <button
        v-if="showCloseButton"
        @click="$emit('close')"
        class="text-white hover:text-red-400 transition-colors bg-black/50 rounded-full p-1 ml-1"
        title="Close Player"
      >
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path
            fill-rule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clip-rule="evenodd"
          ></path>
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import awsExports from "../aws-exports";

// Props
const props = defineProps({
  showCloseButton: {
    type: Boolean,
    default: false,
  },
  initialSource: {
    type: String,
    default: "",
  },
  initialSourceName: {
    type: String,
    default: "",
  },
  initialAudioState: {
    type: Object,
    default: () => ({
      selectedAudioPair: 1,
      volume: 50,
      leftChannelMuted: false,
      rightChannelMuted: false,
      isMuted: false
    }),
  },
  additionalSources: {
    type: Array,
    default: () => [],
  },
  showSourceControl: {
    type: Boolean,
    default: true,
  },
});

// Emits
const emit = defineEmits(["close", "source-changed", "audio-state-changed"]);

// Refs
const videoElement = ref(null);

// Reactive data
const selectedSource = ref(props.initialSource);
const selectedAudioPair = ref(props.initialAudioState.selectedAudioPair);
const volume = ref(props.initialAudioState.volume);
const isMuted = ref(props.initialAudioState.isMuted);
const leftChannelMuted = ref(props.initialAudioState.leftChannelMuted);
const rightChannelMuted = ref(props.initialAudioState.rightChannelMuted);
const isPlaying = ref(false);
const connectionStatus = ref("disconnected");

// Default sources (WHEP URLs)
const defaultSources = [
  {
    id: 6,
    name: "Test Stream (Structured)",
    url: "https://monitor.elas.tech/webrtc/adm/239.61.33.1/61105/whep",
    config: {
      host: "monitor.elas.tech",
      port: 8889, // Not used due to /webrtc redirect
      protocol: "https",
      subPath: "webrtc/adm",
      streamIP: "239.61.33.1",
      streamPort: 61105,
    },
  },
];

// Combined available sources (default + additional from props)
const availableSources = computed(() => {
  return [...defaultSources, ...props.additionalSources].sort((a, b) => a.name.localeCompare(b.name));
});

// Audio pairs (8 pairs)
const audioPairs = ref([
  { id: 1, name: "Pair 1 (Ch 1-2)" },
  { id: 2, name: "Pair 2 (Ch 3-4)" },
  { id: 3, name: "Pair 3 (Ch 5-6)" },
  { id: 4, name: "Pair 4 (Ch 7-8)" },
  { id: 5, name: "Pair 5 (Ch 9-10)" },
  { id: 6, name: "Pair 6 (Ch 11-12)" },
  { id: 7, name: "Pair 7 (Ch 13-14)" },
  { id: 8, name: "Pair 8 (Ch 15-16)" },
]);

// WHEP Client implementation
class WHEPClient {
  constructor(url, videoElement, sourceConfig = null) {
    this.url = url;
    this.videoElement = videoElement;
    this.pc = null;
    this.sessionId = null;
    this.sourceConfig = sourceConfig;

    // Setup AWS Cognito token authentication
    this.awsConfig = awsExports[process.env.VUE_APP_MODE || "development"];
    this.userSub = localStorage.getItem(
      `CognitoIdentityServiceProvider.${this.awsConfig.aws_user_pools_web_client_id}.LastAuthUser`
    );
    this.idTokenKey = `CognitoIdentityServiceProvider.${this.awsConfig.aws_user_pools_web_client_id}.${this.userSub}.idToken`;
  }

  getAuthHeaders() {
    const headers = {
      "Content-Type": "application/sdp",
      Accept: "application/sdp, text/plain;q=0.9,*/*;q=0.8",
    };

    // Priority 1: Use custom token from source config if available
    if (this.sourceConfig && this.sourceConfig.token) {
      headers["Authorization"] = this.sourceConfig.token;
      this.log(`Added custom Authorization header: ${this.sourceConfig.token}`);
      return headers;
    }

    // Priority 2: Use AWS Cognito token if available
    if (this.userSub) {
      const idToken = localStorage.getItem(this.idTokenKey);
      if (idToken) {
        headers["Authorization"] = `Bearer ${idToken}`;
        this.log("Added Authorization header with AWS Cognito Bearer token");
      } else {
        this.log("No idToken found in localStorage");
      }
    } else {
      this.log("No authenticated user found");
    }

    return headers;
  }

  log(message) {
    const logMessage = `[WHEP Client] ${message}`;
    console.log(logMessage);
    // Update the last log entry for display
    lastLogEntry.value = message;
  }

  async play() {
    this.log("Creating RTCPeerConnection");
    connectionStatus.value = "connecting";
    lastLogEntry.value = "Setting up connection...";
    
    this.pc = new RTCPeerConnection({
      iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },
        { urls: "stun:stun2.l.google.com:19302" }
      ],
      iceCandidatePoolSize: 10, // Pre-gather candidates faster
      iceTransportPolicy: "all" // Use all available transport methods
    });

    this.pc.oniceconnectionstatechange = () => {
      this.log(`ICE state: ${this.pc.iceConnectionState}`);
      if (
        this.pc.iceConnectionState === "connected" ||
        this.pc.iceConnectionState === "completed"
      ) {
        connectionStatus.value = "connected";
      } else if (
        this.pc.iceConnectionState === "failed" ||
        this.pc.iceConnectionState === "disconnected"
      ) {
        connectionStatus.value = "disconnected";
      } else if (this.pc.iceConnectionState === "checking") {
        connectionStatus.value = "connecting";
      }
    };

    this.pc.onconnectionstatechange = () => {
      this.log(`PC state: ${this.pc.connectionState}`);
    };

    this.pc.ontrack = (evt) => {
      this.log("Track received (video+audio)");
      this.videoElement.srcObject = evt.streams[0];
      // CRITICAL: Mute the video element so audio only goes through Web Audio API
      this.videoElement.muted = true;
      this.videoElement.volume = 0;
      this.videoElement
        .play()
        .then(() => {
          this.log("Autoplay OK");
          isPlaying.value = true;
          // Set up Web Audio API for channel control
          setupChannelControl(this.videoElement);
        })
        .catch((err) => {
          this.log("Autoplay blocked: " + err);
          isPlaying.value = false;
        });
    };

    this.pc.addTransceiver("video", { direction: "recvonly" });
    this.pc.addTransceiver("audio", { direction: "recvonly" });

    this.log("Creating offer");
    lastLogEntry.value = "Creating connection offer...";
    const offer = await this.pc.createOffer();
    let sdp = offer.sdp;

    // Prefer stereo Opus
    sdp = sdp.replace(
      /(a=rtpmap:(\d+) opus\/48000\/2\r?\n)/g,
      (m, full, pt) => full + `a=fmtp:${pt} stereo=1;sprop-stereo=1\r\n`
    );

    // Check if peer connection is still valid before setting local description
    if (!this.pc) {
      throw new Error("Peer connection was closed during offer creation");
    }
    
    await this.pc.setLocalDescription({ type: "offer", sdp });

    this.log("Waiting ICE gathering");
    lastLogEntry.value = "Gathering network candidates...";
    
    // Use a timeout for ICE gathering to prevent long waits
    const iceGatheringTimeout = 5000; // 5 seconds max
    await new Promise((res) => {
      if (!this.pc) return res();
      if (this.pc.iceGatheringState === "complete") return res();
      
      // Set up timeout to resolve early if ICE gathering takes too long
      const timeout = setTimeout(() => {
        this.log(`ICE gathering timeout after ${iceGatheringTimeout}ms - proceeding with available candidates`);
        lastLogEntry.value = "Using available candidates...";
        res();
      }, iceGatheringTimeout);
      
      this.pc.onicegatheringstatechange = () => {
        if (!this.pc || this.pc.iceGatheringState === "complete") {
          clearTimeout(timeout);
          if (this.pc) {
            this.log("ICE gathering completed");
            lastLogEntry.value = "Network candidates ready";
          }
          res();
        }
      };
    });

    this.log("POST SDP to " + this.url);
    lastLogEntry.value = "Negotiating with server...";
    const headers = this.getAuthHeaders();

    const attemptFetch = async (targetUrl, attemptLabel) => {
      this.log(`Sending WHEP offer (${attemptLabel}) => ${targetUrl}`);
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), 20000); // 20s timeout

      let resp;
      try {
        // Check if peer connection and local description are still valid
        if (!this.pc || !this.pc.localDescription) {
          throw new Error("Peer connection or local description is no longer available");
        }
        
        resp = await fetch(targetUrl, {
          method: "POST",
          headers,
          body: this.pc.localDescription.sdp,
          signal: controller.signal,
          mode: "cors",
        });
      } catch (e) {
        clearTimeout(t);
        throw e;
      }
      clearTimeout(t);

      if (!resp.ok) {
        // Try to get the actual error message from the response body
        let errorMessage = `WHEP offer failed ${resp.status}`;
        try {
          const responseText = await resp.text();
          if (responseText && responseText.trim()) {
            // Check if response is JSON with error field
            try {
              const jsonError = JSON.parse(responseText);
              if (jsonError.error) {
                errorMessage = jsonError.error;
              } else if (jsonError.message) {
                errorMessage = jsonError.message;
              } else {
                errorMessage = responseText;
              }
            } catch {
              // Not JSON, use as plain text
              errorMessage = responseText;
            }
          }
        } catch (textError) {
          console.warn("Could not read error response body:", textError);
        }
        
        const error = new Error(errorMessage);
        this.log(`WHEP offer failed: ${errorMessage}`);
        throw error;
      }
      return resp;
    };

    let response;
    try {
      response = await attemptFetch(this.url, "primary");
    } catch (err) {
      // Network error fallback: if https with custom port, retry with http
      const u = new URL(this.url);
      if (
        (err.name === "TypeError" || err.message.includes("NetworkError")) &&
        u.protocol === "https:" &&
        u.port &&
        u.port !== "443"
      ) {
        const fallback = "http://" + u.host + u.pathname + u.search;
        this.log(
          `Primary fetch failed (${err.message}). Attempting fallback without TLS: ${fallback}`
        );
        try {
          response = await attemptFetch(fallback, "fallback-http");
          this.url = fallback;
        } catch (err2) {
          this.log("Fallback attempt failed: " + err2.message);
          throw err;
        }
      } else {
        throw err;
      }
    }

    const answer = await response.text();
    let loc = response.headers.get("Location");

    if (!loc) {
      this.log("No Location header; using offer URL as session");
      this.sessionId = this.url;
    } else if (/^https?:/i.test(loc)) {
      this.sessionId = loc;
    } else if (loc.startsWith("/")) {
      const origin = new URL(this.url).origin;
      this.sessionId = origin + loc;
    } else {
      this.sessionId = new URL(loc, this.url).toString();
    }

    this.log("Session URL: " + this.sessionId);
    this.log("Setting remote description");
    
    // Check if peer connection is still valid before setting remote description
    if (!this.pc) {
      throw new Error("Peer connection was closed during setup");
    }
    
    await this.pc.setRemoteDescription({ type: "answer", sdp: answer });
  }

  async stop() {
    this.log("Stopping client");
    if (this.pc) {
      this.pc.getReceivers().forEach((rcv) => rcv.track && rcv.track.stop());
      this.pc.close();
      this.pc = null;
    }

    if (this.sessionId) {
      try {
        const deleteHeaders = {};
        // Add bearer token for DELETE request
        if (this.userSub) {
          const idToken = localStorage.getItem(this.idTokenKey);
          if (idToken) {
            deleteHeaders["Authorization"] = `Bearer ${idToken}`;
          }
        }

        await fetch(this.sessionId, {
          method: "DELETE",
          headers: deleteHeaders,
        });
        this.log("Session deleted");
      } catch (e) {
        this.log("Delete failed: " + e);
      }
      this.sessionId = null;
    }

    this.videoElement.pause();
    this.videoElement.srcObject = null;
  }

  async eject() {
    this.log("Ejecting (DELETE session, keep player visible)");
    if (this.sessionId) {
      try {
        const deleteHeaders = {};
        // Add bearer token for DELETE request
        if (this.userSub) {
          const idToken = localStorage.getItem(this.idTokenKey);
          if (idToken) {
            deleteHeaders["Authorization"] = `Bearer ${idToken}`;
          }
        }

        await fetch(this.sessionId, {
          method: "DELETE",
          headers: deleteHeaders,
        });
        this.log("Session ejected");
      } catch (e) {
        this.log("Eject failed: " + e);
      }
      this.sessionId = null;
    }

    if (this.pc) {
      this.pc.getReceivers().forEach((r) => r.track && r.track.stop());
    }
  }
}

// WebRTC client instance
let whepClient = null;

// Audio channel states
const channelStates = ref({ 
  left: props.initialAudioState.leftChannelMuted, 
  right: props.initialAudioState.rightChannelMuted 
});
const volumeStates = ref(props.initialAudioState.volume);

// Last log entry for display
const lastLogEntry = ref("");

// Helper function to emit audio state changes
const emitAudioStateChange = () => {
  const audioState = {
    selectedAudioPair: selectedAudioPair.value,
    volume: volume.value,
    leftChannelMuted: leftChannelMuted.value,
    rightChannelMuted: rightChannelMuted.value,
    isMuted: isMuted.value
  };
  emit('audio-state-changed', audioState);
};

// Helper function to apply audio state to existing audio context
const applyAudioStateToContext = () => {
  if (videoElement.value && videoElement.value._channelSetup) {
    const leftGainValue = leftChannelMuted.value ? 0 : 1;
    const rightGainValue = rightChannelMuted.value ? 0 : 1;
    
    videoElement.value._leftGain.gain.value = leftGainValue;
    videoElement.value._rightGain.gain.value = rightGainValue;
    videoElement.value._masterVolume.gain.value = volume.value / 100;
    
    console.log('🔊 Applied audio state to existing context:', {
      left: leftGainValue,
      right: rightGainValue,
      volume: volume.value
    });
  }
};

// Computed properties
const currentSourceName = computed(() => {
  // If we have an initial source name from props, use that first
  if (props.initialSourceName) {
    return props.initialSourceName;
  }
  
  // Fallback to looking up by URL (original behavior)
  if (!selectedSource.value) return null;
  const source = availableSources.value.find((s) => s.url === selectedSource.value);
  return source ? source.name : "Unknown Source";
});

// Methods
const changeSource = async () => {
  if (!selectedSource.value) return;

  // Emit source change to parent component
  emit('source-changed', selectedSource.value);

  try {
    connectionStatus.value = "connecting";
    lastLogEntry.value = "Connecting...";
    await connectToWebRTC(selectedSource.value);
  } catch (error) {
    console.error("Failed to connect to source:", error);
    connectionStatus.value = "disconnected";
    
    // Display the actual server error message if available
    let errorMessage = error.message;
    
    // Check if it's a server error with specific message
    if (errorMessage.includes('no stream is available') || 
        errorMessage.includes('stream not found') ||
        errorMessage.includes('path')) {
      // Server provided a specific error - use it directly
      lastLogEntry.value = `Error: ${errorMessage}`;
    } else if (errorMessage.includes('404')) {
      lastLogEntry.value = "Error: Stream not found (404) - Check source path";
    } else if (errorMessage.includes('403')) {
      lastLogEntry.value = "Error: Access denied (403) - Check authentication";
    } else if (errorMessage.includes('500')) {
      lastLogEntry.value = "Error: Server error (500) - Try again later";
    } else if (errorMessage.includes('WHEP offer failed')) {
      const statusMatch = errorMessage.match(/WHEP offer failed (\d+)/);
      const status = statusMatch ? statusMatch[1] : 'unknown';
      lastLogEntry.value = `Error: WHEP failed (${status})`;
    } else if (errorMessage.includes('NetworkError') || errorMessage.includes('TypeError')) {
      lastLogEntry.value = "Error: Network connection failed";
    } else if (errorMessage.includes('timeout') || errorMessage.includes('AbortError')) {
      lastLogEntry.value = "Error: Connection timeout";
    } else {
      lastLogEntry.value = `Error: ${errorMessage}`;
    }
  }
};

const seamlessReconnect = async () => {
  if (!selectedSource.value) return;

  try {
    // Keep current connection while establishing new one
    const currentClient = whepClient;
    connectionStatus.value = "connecting";
    lastLogEntry.value = "Switching audio channel...";

    // Build new URL with audio pair
    const sourceUrl = selectedSource.value;
    let finalUrl = sourceUrl;
    const audioPairMap = {
      1: 'a1', 2: 'a2', 3: 'a3', 4: 'a4',
      5: 'a5', 6: 'a6', 7: 'a7', 8: 'a8'
    };
    const audioPair = audioPairMap[selectedAudioPair.value] || 'a1';
    
    // Replace existing audio pair or add new one
    if (finalUrl.includes('/a1') || finalUrl.includes('/a2') || 
        finalUrl.includes('/a3') || finalUrl.includes('/a4') ||
        finalUrl.includes('/a5') || finalUrl.includes('/a6') ||
        finalUrl.includes('/a7') || finalUrl.includes('/a8')) {
      // Replace existing audio pair
      finalUrl = finalUrl.replace(/\/a[1-8]\//, `/${audioPair}/`);
    } else {
      // Add audio pair before /whep
      finalUrl = finalUrl.replace('/whep', `/${audioPair}/whep`);
    }

    console.log(`Seamless reconnect with audio pair ${audioPair}: ${finalUrl}`);

    // Find source config for the selected URL
    const sourceConfig = availableSources.value.find((s) => s.url === selectedSource.value)?.config || null;

    // Create new WHEP client
    const newClient = new WHEPClient(finalUrl, videoElement.value, sourceConfig);

    // Start new connection
    await newClient.play();
    
    // If successful, replace the old client WITHOUT stopping it
    whepClient = newClient;
    connectionStatus.value = "connected";
    
    // Just abandon the old client without sending DELETE requests
    if (currentClient) {
      console.log("Abandoning old connection (no DELETE sent)");
      // Only close the peer connection locally, no server DELETE
      if (currentClient.pc) {
        currentClient.pc.getReceivers().forEach((rcv) => rcv.track && rcv.track.stop());
        currentClient.pc.close();
        currentClient.pc = null;
      }
      // Clear session ID so destructor won't send DELETE
      currentClient.sessionId = null;
    }

    // Setup audio context for new stream
    if (videoElement.value) {
      // Clean up old audio context first
      cleanupAudioContext(videoElement.value);
      // Note: Preserving existing channel states during reconnect
    }

  } catch (error) {
    console.error("Seamless reconnect failed:", error);
    connectionStatus.value = "disconnected";
    
    // Display the actual server error message if available
    let errorMessage = error.message;
    if (errorMessage.includes('no stream is available') || 
        errorMessage.includes('stream not found') ||
        errorMessage.includes('path')) {
      lastLogEntry.value = `Error: ${errorMessage}`;
    } else if (errorMessage.includes('404')) {
      lastLogEntry.value = "Error: Audio channel not found (404)";
    } else if (errorMessage.includes('403')) {
      lastLogEntry.value = "Error: Access denied (403)";
    } else {
      lastLogEntry.value = `Error: ${errorMessage}`;
    }
  }
};

const connectToWebRTC = async (sourceUrl) => {
  // Stop existing connection
  if (whepClient) {
    await whepClient.stop();
    whepClient = null;
  }

  if (!videoElement.value) {
    throw new Error("Video element not available");
  }

  // Clean up existing audio context
  if (videoElement.value) {
    cleanupAudioContext(videoElement.value);
  }

  // Note: Preserving existing channel states - don't reset to muted

  // Build URL with audio pair
  let finalUrl = sourceUrl;
  const audioPairMap = {
    1: 'a1', 2: 'a2', 3: 'a3', 4: 'a4',
    5: 'a5', 6: 'a6', 7: 'a7', 8: 'a8'
  };
  const audioPair = audioPairMap[selectedAudioPair.value] || 'a1';
  
  // Replace existing audio pair or add new one (same logic as seamlessReconnect)
  if (finalUrl.includes('/a1') || finalUrl.includes('/a2') || 
      finalUrl.includes('/a3') || finalUrl.includes('/a4') ||
      finalUrl.includes('/a5') || finalUrl.includes('/a6') ||
      finalUrl.includes('/a7') || finalUrl.includes('/a8')) {
    // Replace existing audio pair
    finalUrl = finalUrl.replace(/\/a[1-8]\//, `/${audioPair}/`);
  } else {
    // Add audio pair before /whep
    finalUrl = finalUrl.replace('/whep', `/${audioPair}/whep`);
  }

  console.log(`Connecting with audio pair ${audioPair}: ${finalUrl}`);

  // Find source config for the selected URL
  const sourceConfig = availableSources.value.find((s) => s.url === sourceUrl)?.config || null;

  // Create new WHEP client with source config
  whepClient = new WHEPClient(finalUrl, videoElement.value, sourceConfig);

  try {
    await whepClient.play();
    connectionStatus.value = "connected";
  } catch (error) {
    connectionStatus.value = "disconnected";
    // Log the error through WHEP client for display
    if (whepClient) {
      whepClient.log(`Connection failed: ${error.message}`);
    }
    throw error;
  }
};

const changeAudioPair = async () => {
  console.log("Changed audio pair to:", selectedAudioPair.value);
  // Reconnect with new audio pair if source is selected
  if (selectedSource.value) {
    await changeSource();
    // Use seamless reconnect to avoid blocking
    // await seamlessReconnect();
  }
};

// Removed toggleMute - using Web Audio API channel control instead

const toggleLeftChannel = async () => {
  await toggleChannel('left');
};

const toggleRightChannel = async () => {
  await toggleChannel('right');
};

const toggleMasterAudio = async () => {
  const leftMuted = channelStates.value.left;
  const rightMuted = channelStates.value.right;
  const bothMuted = leftMuted && rightMuted;

  if (bothMuted) {
    // Both muted - unmute both
    await toggleChannel('left');
    await toggleChannel('right');
  } else {
    // At least one unmuted - mute both
    if (!leftMuted) {
      await toggleChannel('left');
    }
    if (!rightMuted) {
      await toggleChannel('right');
    }
  }
};

const getVolumeButtonTitle = () => {
  const leftMuted = channelStates.value.left;
  const rightMuted = channelStates.value.right;
  const bothMuted = leftMuted && rightMuted;
  
  if (bothMuted) {
    return 'Channels Muted - Click to unmute';
  } else if (volume.value === 0) {
    return 'Volume Muted - Click to mute channels';
  } else {
    return `Volume: ${volume.value}% - Click to mute channels`;
  }
};

const changeVolume = () => {
  if (videoElement.value && videoElement.value._masterVolume) {
    videoElement.value._masterVolume.gain.value = volume.value / 100;
    volumeStates.value = volume.value;
    console.log(`Volume set to ${volume.value}%`);
  }
};

// Audio channel control functions
const setupChannelControl = async (videoEl) => {
  if (videoEl._channelSetup) {
    console.log("Channel control already set up");
    return;
  }

  console.log("Setting up channel control");

  try {
    // Wait for srcObject to be available
    if (!videoEl.srcObject) {
      console.log("Waiting for srcObject...");
      setTimeout(() => setupChannelControl(videoEl), 100);
      return;
    }

    // CRITICAL: Ensure video element is muted
    videoEl.muted = true;
    videoEl.volume = 0;

    console.log("Creating audio context...");
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    console.log(`AudioContext created, state: ${audioContext.state}`);

    // Get the MediaStream from the video element
    const stream = videoEl.srcObject;
    console.log("Got MediaStream:", stream);

    if (!stream) {
      throw new Error("No MediaStream available on video element");
    }

    // Check audio tracks
    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length > 0) {
      const audioTrack = audioTracks[0];
      const settings = audioTrack.getSettings();
      console.log("Audio track settings:", settings);
      console.log(`Channel count: ${settings.channelCount || "unknown"}`);
    }

    // Create MediaStreamSource
    console.log("Creating MediaStreamSource...");
    const source = audioContext.createMediaStreamSource(stream);
    console.log("MediaStreamSource created");

    // Build the channel splitting audio graph with master volume
    const splitter = audioContext.createChannelSplitter(2);
    const leftGain = audioContext.createGain();
    const rightGain = audioContext.createGain();
    const merger = audioContext.createChannelMerger(2);
    const masterVolume = audioContext.createGain();

    // Set initial channel and volume states from restored values
    const leftGainValue = channelStates.value.left ? 0 : 1;
    const rightGainValue = channelStates.value.right ? 0 : 1;
    leftGain.gain.value = leftGainValue;
    rightGain.gain.value = rightGainValue;
    masterVolume.gain.value = volume.value / 100;
    console.log(`Channel setup: left=${leftGainValue} (muted=${channelStates.value.left}), right=${rightGainValue} (muted=${channelStates.value.right}), volume=${volume.value}%`);

    // Connect the audio graph
    console.log("Connecting audio nodes...");
    source.connect(splitter);
    splitter.connect(leftGain, 0);
    splitter.connect(rightGain, 1);
    leftGain.connect(merger, 0, 0);
    rightGain.connect(merger, 0, 1);
    merger.connect(masterVolume);
    masterVolume.connect(audioContext.destination);
    console.log("Audio graph connected");

    // Store references
    videoEl._audioContext = audioContext;
    videoEl._leftGain = leftGain;
    videoEl._rightGain = rightGain;
    videoEl._masterVolume = masterVolume;
    videoEl._channelSetup = true;

    console.log("✅ Channel control ready");
  } catch (error) {
    console.error("❌ Channel control setup failed:", error);
    lastLogEntry.value = `Audio setup failed: ${error.message}`;
  }
};

const toggleChannel = async (channel) => {
  if (!videoElement.value || !videoElement.value._channelSetup) {
    console.log("Channel control not ready");
    return;
  }

  const videoEl = videoElement.value;

  // Ensure audio context is running (user interaction)
  if (videoEl._audioContext && videoEl._audioContext.state === "suspended") {
    console.log("Resuming audio context on user interaction...");
    await videoEl._audioContext.resume();
    console.log(`AudioContext resumed, new state: ${videoEl._audioContext.state}`);
  }

  // Toggle state
  channelStates.value[channel] = !channelStates.value[channel];
  const leftMuted = channelStates.value.left;
  const rightMuted = channelStates.value.right;

  // Control individual channel gains
  const leftGainValue = leftMuted ? 0 : 1;
  const rightGainValue = rightMuted ? 0 : 1;

  videoEl._leftGain.gain.value = leftGainValue;
  videoEl._rightGain.gain.value = rightGainValue;

  // Update reactive state for UI
  leftChannelMuted.value = leftMuted;
  rightChannelMuted.value = rightMuted;

  console.log(`Channels: L=${leftMuted ? "OFF" : "ON"} (${leftGainValue}), R=${rightMuted ? "OFF" : "ON"} (${rightGainValue})`);
};

const cleanupAudioContext = (videoEl) => {
  if (videoEl._audioContext) {
    try {
      if (videoEl._audioContext.state !== "closed") {
        videoEl._audioContext.close();
      }
    } catch (e) {
      console.warn("Error closing audio context:", e);
    }
    delete videoEl._audioContext;
    delete videoEl._leftGain;
    delete videoEl._rightGain;
    delete videoEl._masterVolume;
    delete videoEl._channelSetup;
  }
};

const togglePlayPause = () => {
  if (!videoElement.value) return;

  if (isPlaying.value) {
    videoElement.value.pause();
    isPlaying.value = false;
  } else {
    videoElement.value
      .play()
      .then(() => {
        isPlaying.value = true;
      })
      .catch((err) => {
        console.error("Play failed:", err);
      });
  }
};

const toggleFullscreen = () => {
  if (!videoElement.value) return;

  if (document.fullscreenElement) {
    document.exitFullscreen();
  } else {
    videoElement.value.requestFullscreen().catch((err) => {
      console.error("Fullscreen failed:", err);
    });
  }
};

const ejectSource = async () => {
  selectedSource.value = "";

  if (whepClient) {
    try {
      await whepClient.eject();
    } catch (error) {
      console.error("Eject failed:", error);
    }
  }

  // Clean up audio context
  if (videoElement.value) {
    cleanupAudioContext(videoElement.value);
  }

  isPlaying.value = false;
  connectionStatus.value = "disconnected";
  
  // Reset channel states
  channelStates.value = { left: true, right: true };
  leftChannelMuted.value = true;
  rightChannelMuted.value = true;
};

const stopSource = async () => {
  if (whepClient) {
    try {
      await whepClient.stop();
      whepClient = null;
    } catch (error) {
      console.error("Stop failed:", error);
    }
  }

  // Clean up audio context
  if (videoElement.value) {
    cleanupAudioContext(videoElement.value);
  }

  selectedSource.value = "";
  isPlaying.value = false;
  connectionStatus.value = "disconnected";
  
  // Reset channel states
  channelStates.value = { left: true, right: true };
  leftChannelMuted.value = true;
  rightChannelMuted.value = true;
};

// Lifecycle
onMounted(() => {
  console.log('🎵 MvPlayerWebRtc mounted with initialAudioState:', props.initialAudioState);
  console.log('🎵 Current reactive audio state:', {
    selectedAudioPair: selectedAudioPair.value,
    volume: volume.value,
    leftChannelMuted: leftChannelMuted.value,
    rightChannelMuted: rightChannelMuted.value,
    isMuted: isMuted.value
  });

  if (videoElement.value) {
    videoElement.value.volume = volume.value / 100;

    // Listen for video events
    videoElement.value.addEventListener("play", () => {
      isPlaying.value = true;
    });

    videoElement.value.addEventListener("pause", () => {
      isPlaying.value = false;
    });

    videoElement.value.addEventListener("loadstart", () => {
      connectionStatus.value = "connecting";
    });

    videoElement.value.addEventListener("canplay", () => {
      connectionStatus.value = "connected";
    });

    videoElement.value.addEventListener("error", (e) => {
      console.error("Video error:", e);
      connectionStatus.value = "disconnected";
    });
  }

  // Handle browser/tab close or navigation away
  const handleBeforeUnload = () => {
    if (whepClient) {
      // Use eject for graceful session cleanup when leaving
      whepClient.eject().catch((error) => {
        console.warn("Failed to eject source on beforeunload:", error);
      });
    }
  };
  
  window.addEventListener('beforeunload', handleBeforeUnload);
  
  // Store reference to remove listener on unmount
  window._mvPlayerBeforeUnloadHandler = handleBeforeUnload;

  // Auto-connect if initial source is provided
  if (props.initialSource) {
    selectedSource.value = props.initialSource;
    changeSource();
  }
});

onUnmounted(async () => {
  // Remove beforeunload event listener
  if (window._mvPlayerBeforeUnloadHandler) {
    window.removeEventListener('beforeunload', window._mvPlayerBeforeUnloadHandler);
    delete window._mvPlayerBeforeUnloadHandler;
  }
  
  if (whepClient) {
    try {
      await whepClient.eject();
    } catch (error) {
      console.error("Eject cleanup failed:", error);
    }
  }
  
  // Clean up audio context
  if (videoElement.value) {
    cleanupAudioContext(videoElement.value);
  }
});

// Watch for source changes
watch(
  () => props.initialSource,
  (newSource) => {
    selectedSource.value = newSource;
    if (newSource) {
      changeSource();
    }
  }
);

// Watch for selectedSource changes to emit updates
watch(selectedSource, (newSource) => {
  if (newSource !== props.initialSource) {
    emit('source-changed', newSource);
  }
});

// Watch for audio state changes to emit updates
watch([selectedAudioPair, volume, leftChannelMuted, rightChannelMuted, isMuted], () => {
  emitAudioStateChange();
}, { deep: true });

// Watch for initial audio state changes from props
watch(() => props.initialAudioState, (newAudioState) => {
  console.log('🔄 Props audio state changed, updating component state:', newAudioState);
  selectedAudioPair.value = newAudioState.selectedAudioPair;
  volume.value = newAudioState.volume;
  leftChannelMuted.value = newAudioState.leftChannelMuted;
  rightChannelMuted.value = newAudioState.rightChannelMuted;
  isMuted.value = newAudioState.isMuted;
  
  // Update internal channel states
  channelStates.value = {
    left: newAudioState.leftChannelMuted,
    right: newAudioState.rightChannelMuted
  };
  volumeStates.value = newAudioState.volume;
  
  console.log('🔄 Updated component internal state:', {
    channelStates: channelStates.value,
    volume: volume.value,
    leftChannelMuted: leftChannelMuted.value,
    rightChannelMuted: rightChannelMuted.value
  });
  
  // Apply to existing audio context if available
  applyAudioStateToContext();
}, { deep: true });

// Expose methods for parent components
defineExpose({
  ejectSource,
  stopSource,
  changeSource,
  changeAudioPair,
  connectionStatus: computed(() => connectionStatus.value),
  selectedSource: computed(() => selectedSource.value)
});
</script>

<style scoped>
/* Custom slider styling */
.slider::-webkit-slider-thumb {
  appearance: none;
  height: 16px;
  width: 16px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
}

.slider::-moz-range-thumb {
  height: 16px;
  width: 16px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  border: none;
}

/* Vertical slider styling */
.slider-vertical {
  writing-mode: bt-lr; /* IE */
  -webkit-appearance: slider-vertical; /* WebKit */
  appearance: slider-vertical; /* Standard */
  width: 8px !important;
  outline: none;
}

.slider-vertical::-webkit-slider-thumb {
  appearance: none;
  height: 12px;
  width: 12px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  border: none;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
}

.slider-vertical::-moz-range-thumb {
  height: 12px;
  width: 12px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  border: none;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
}

.slider-vertical::-webkit-slider-track {
  background: #4b5563;
  border-radius: 4px;
  width: 8px;
}

.slider-vertical::-moz-range-track {
  background: #4b5563;
  border-radius: 4px;
  width: 8px;
  border: none;
}

/* Firefox vertical slider fix */
.slider-vertical::-moz-range-track {
  height: 80px;
  width: 8px;
}

/* Alternative approach for browsers that don't support vertical sliders */
@supports not (-webkit-appearance: slider-vertical) {
  .slider-vertical {
    transform: rotate(-90deg);
    transform-origin: center;
    width: 80px !important;
    height: 8px !important;
    margin: 36px 0;
  }
}
</style>

